/* eslint-disable no-nested-ternary */
import React, { useMemo, useState, useEffect } from 'react';
import groupBy from 'lodash/groupBy';
import forEach from 'lodash/forEach';
import _ from 'lodash';
import findIndex from 'lodash/findIndex';
import swal from 'sweetalert2';
import moment from 'moment';
import API from '../../../middleware/API';
import { ScrollSync, ScrollSyncNode } from '../../util/ScrollSync';
import { monthNames, yearNames, GetStoreRole, GetStoreDetailRole } from '../../../const/constant';
import AddWorkTimeInSignInShiftBlock from '../../AddWorkTimeInSignInShiftBlock';
import '../../../scss/components/pages/shiftsheet.scss';

const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

const JOB_TYPES = {
  innerFormal: ['內場正職'],
  innerPartime: ['內場兼職'],
  outsideFormal: ['外場正職'],
  outsidePartime: ['外場兼職'],
  barFormal: ['吧檯正職'],
  barPartime: ['吧檯兼職'],
};

const getMonthDayAmount = (targetYear, targetMonth) => new Date(targetYear, targetMonth + 1, 0).getDate();

const getDateKey = (targetYear, targetMonth, day) => `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const hasLetters = (str) => /[a-z]/i.test(str.toLowerCase());

const getDisplayTime = (value) => String(value || '').replace(':', '');

const canManageShift = (role) => role === 1 || role === 2 || role === 5 || role === '1' || role === '2' || role === '5';

const normalizeTime = (value) => {
  if (!value) {
    return '';
  }
  const text = String(value);
  const hhmm = text.length >= 5 ? text.substring(0, 5) : text;
  if (hhmm.length < 2) {
    return '';
  }
  return hhmm.substring(3, 5) !== '00' ? hhmm : hhmm.substring(0, 2);
};

const getActualTimeRangeFromData = (recordData = []) => {
  if (!Array.isArray(recordData) || recordData.length === 0) {
    return { signIn: '', signOut: '' };
  }

  const signInList = recordData
    .map((item) => item.actualSignInTime)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const signOutList = recordData
    .map((item) => item.actualSignOutTime)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  return {
    signIn: normalizeTime(signInList[0]),
    signOut: normalizeTime(signOutList[signOutList.length - 1]),
  };
};

const hasAnyTime = (timeRange = {}) => Boolean(timeRange.signIn || timeRange.signOut);

const ShiftSheet = () => {
  const [activityList, setActivityList] = useState([]);
  const [holidayList, setHolidayList] = useState([]);
  const [finalRecords, setFinalRecords] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [reload, doReload] = useState(false);
  const [isAddWorkTimeOpen, setIsAddWorkTimeOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [isRequestComplete, setIsRequestComplete] = useState(true);
  const [dept, setDept] = useState('all');
  const [timeViewMode, setTimeViewMode] = useState('expected');

  const currentRole = GetStoreRole(localStorage.getItem('current_store_id'));
  const currentDetailRole = GetStoreDetailRole(localStorage.getItem('current_store_id'));
  const dayAmount = useMemo(() => getMonthDayAmount(parseInt(year, 10), parseInt(month, 10)), [year, month]);

  const dayNumbers = useMemo(() => Array.from({ length: dayAmount }, (_, idx) => idx + 1), [dayAmount]);

  const weekdayByDay = useMemo(() => {
    const baseWeekday = new Date(parseInt(year, 10), parseInt(month, 10), 1).getDay();
    const map = {};
    dayNumbers.forEach((day) => {
      map[day] = dayNames[(day - 1 + baseWeekday) % 7];
    });
    return map;
  }, [dayNumbers, month, year]);

  const eventByDay = useMemo(() => {
    const map = {};
    dayNumbers.forEach((day) => {
      const dateKey = getDateKey(parseInt(year, 10), parseInt(month, 10), day);
      map[day] = {
        activities: activityList.filter((event) => event.expectedDate === dateKey),
        holidays: holidayList.filter((event) => event.expectedDate === dateKey),
      };
    });
    return map;
  }, [activityList, dayNumbers, holidayList, month, year]);

  const workerCountByDay = useMemo(() => {
    const createEmpty = () => {
      const count = {};
      dayNumbers.forEach((day) => {
        count[day] = 0;
      });
      return count;
    };

    const counts = {
      innerFormalCount: createEmpty(),
      innerPartimeCount: createEmpty(),
      outsideFormalCount: createEmpty(),
      outsidePartimeCount: createEmpty(),
      barFormalCount: createEmpty(),
      barPartimeCount: createEmpty(),
    };

    finalRecords.forEach((staff) => {
      staff.arrangements.forEach((arrangement) => {
        if (arrangement.resourceId !== 6) {
          return;
        }

        const day = arrangement.expectedDate;
        if (!dayNumbers.includes(day)) {
          return;
        }

        if (JOB_TYPES.innerFormal.some((jobType) => arrangement.jobType.includes(jobType))) {
          counts.innerFormalCount[day] += 1;
        }
        if (JOB_TYPES.innerPartime.some((jobType) => arrangement.jobType.includes(jobType))) {
          counts.innerPartimeCount[day] += 1;
        }
        if (JOB_TYPES.outsideFormal.some((jobType) => arrangement.jobType.includes(jobType))) {
          counts.outsideFormalCount[day] += 1;
        }
        if (JOB_TYPES.outsidePartime.some((jobType) => arrangement.jobType.includes(jobType))) {
          counts.outsidePartimeCount[day] += 1;
        }
        if (JOB_TYPES.barFormal.some((jobType) => arrangement.jobType.includes(jobType))) {
          counts.barFormalCount[day] += 1;
        }
        if (JOB_TYPES.barPartime.some((jobType) => arrangement.jobType.includes(jobType))) {
          counts.barPartimeCount[day] += 1;
        }
      });
    });

    return counts;
  }, [dayNumbers, finalRecords]);

  const handleDropReason = (eventId, realname, date) => {
    const monthStr = parseInt(month, 10) + 1;

    swal({
      title: '確定刪除該事件?',
      text: `${realname}${monthStr}月${date}號排班事件`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#A9ABA8',
      heightAuto: false,
      confirmButtonText: '確定',
      cancelButtonText: '取消',
    }).then((result) => {
      if (!result.value) {
        return;
      }

      const objEvent = {
        data: {
          id: `${eventId}`,
        },
      };

      API.delete('Event', objEvent).then((res) => {
        if (res !== 0) {
          swal({
            title: '刪除成功',
            type: 'success',
          }).then(() => {
            doReload((prevReload) => !prevReload);
          });
        } else {
          swal({
            title: '刪除失敗',
            html: '該事件有送審中或已審核的假單<br/>、加班單或已被簽核。',
            type: 'error',
          });
        }
      });
    });
  };

  const handleSwitchLeave = (eventId, realname, date) => {
    const monthStr = parseInt(month, 10) + 1;

    swal({
      title: '確定切換假別?',
      text: `${realname}${monthStr}月${date}號假別`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#A9ABA8',
      heightAuto: false,
      confirmButtonText: '確定',
      cancelButtonText: '取消',
    }).then((result) => {
      if (!result.value) {
        return;
      }

      API.patch(`SwitchLeave/${eventId}`).then(() => {
        swal({
          title: '切換成功',
          type: 'success',
        }).then(() => {
          doReload((prevReload) => !prevReload);
        });
      });
    });
  };

  const selectElementContents = (el) => {
    const { body } = document;
    let range;
    let sel;
    if (document.createRange && window.getSelection) {
      range = document.createRange();
      sel = window.getSelection();
      sel.removeAllRanges();
      try {
        range.selectNodeContents(el);
        sel.addRange(range);
      } catch (e) {
        range.selectNode(el);
        sel.addRange(range);
      }
    } else if (body.createTextRange) {
      range = body.createTextRange();
      range.moveToElementText(el);
      range.select();
    }
    swal({
      type: 'info',
      title: '已複製到剪貼簿',
      showConfirmButton: false,
      showCloseButton: false,
      width: '250px',
      timer: 1000,
    });
    document.execCommand('Copy');
  };

  const handleScrollChange = () => {
    const fixedHeader = document.getElementById('calendar1');
    if (fixedHeader) {
      fixedHeader.scrollTop = 0;
    }
  };

  useEffect(() => {
    API.get('/User/All').then((response) => {
      if (response) {
        setStaffList(response);
      }
    });
  }, []);

  useEffect(() => {
    API.get(`/Event/AttendanceSheet/Final?year=${parseInt(year, 10)}&month=${parseInt(month, 10) + 1}&dept=${dept}`).then((response) => {
      if (!response) {
        setFinalRecords([]);
        setActivityList([]);
        setHolidayList([]);
        return;
      }

      const groupedRecords = groupBy(response.records || [], (record) => record.name);
      const groupedDaysRecords = (response.records || []).filter((event) => event.resourceId === 6);
      const resultresult = _.countBy(groupedDaysRecords, 'name');
      const preparedRecords = [];

      const reducerSalaryTime = (final, now) => ({ totalSalaryTime: final.totalSalaryTime + now.totalSalaryTime });

      const reducerTimeExpected = (final, now) => {
        let hoursDiff = 0;
        let minutesDiff = 0;
        const start = moment.utc(now.expectedSignInTime, 'HH:mm');
        const end = moment.utc(now.expectedSignOutTime, 'HH:mm');
        const diff = end.diff(start);
        if (!now.absence) {
          hoursDiff = moment.duration(diff).hours();
          minutesDiff = moment.duration(diff).minutes();
          if (hoursDiff > 4 && hoursDiff < 8) {
            if (minutesDiff === 30) {
              minutesDiff = 0;
            } else {
              hoursDiff -= 1;
              minutesDiff = 30;
            }
          } else if (hoursDiff >= 8 && hoursDiff < 12) {
            hoursDiff -= 1;
          } else if (hoursDiff >= 12) {
            hoursDiff -= 2;
          }
        }
        return { hours: final.hours + hoursDiff, minutes: final.minutes + minutesDiff };
      };

      const reducerTimeActual = (finalByDate, nowByDate) => ({ data: nowByDate.data.concat(finalByDate.data) });

      const reducerCalculateTime = (final, now) => {
        const start = moment.utc(now.actualSignInTime, 'HH:mm');
        const end = moment.utc(now.actualSignOutTime, 'HH:mm');
        if (now.actualSignInTime !== null && now.actualSignOutTime !== null) {
          const diff = end.diff(start);
          const hoursDiff = moment.duration(diff).hours();
          const minutesDiff = moment.duration(diff).minutes();
          return { hours: final.hours + hoursDiff, minutes: final.minutes + minutesDiff };
        }
        return { hours: final.hours, minutes: final.minutes };
      };

      forEach(groupedRecords, (value, key) => {
        preparedRecords.push({
          name: key,
          displayName: value[0] && value[0].realName ? value[0].realName : key,
          workDays: resultresult[key] - value.filter((m) => m.resourceId === 9 && value.some((n) => n.resourceId === 6 && n.expectedDate === m.expectedDate)).length,
          timeExpected: value.filter((m) => m.resourceId === 6).reduce(reducerTimeExpected, { hours: 0, minutes: 0 }),
          restDays: value.filter((m) => (m.resourceId === 9 || m.resourceId === 8) && value.some((n) => n.resourceId === 6 && n.expectedDate === m.expectedDate)).length,
          timeActual: value
            .filter((m) => m.resourceId !== 8 && m.resourceId !== 9 && m.resourceId !== 10)
            .reduce(reducerTimeActual, { data: [] })
            .data.reduce(reducerCalculateTime, { hours: 0, minutes: 0 }),
          timeSalary: value.filter((m) => m.resourceId === 6).reduce(reducerSalaryTime, { totalSalaryTime: 0 }),
          arrangements: value.map((arrangement) => ({
            eventId: arrangement.eventId,
            realname: arrangement.name,
            absence: arrangement.absence,
            jobType: arrangement.jobType,
            resourceId: arrangement.resourceId,
            createUserId: arrangement.createUserId,
            hasLeave: arrangement.hasLeave,
            hasOvertime: arrangement.hasOvertime,
            data: arrangement.data || [],
            leaveData: arrangement.leaveData || [],
            expectedDate: new Date(arrangement.expectedDate).getDate(),
            expectedSignInTime:
              arrangement.expectedSignInTime.toString().substring(3, 5) !== '00'
                ? arrangement.expectedSignInTime.toString().substring(0, 5)
                : arrangement.expectedSignInTime.toString().substring(0, 2),
            expectedSignOutTime:
              arrangement.expectedSignOutTime.toString().substring(3, 5) !== '00'
                ? arrangement.expectedSignOutTime.toString().substring(0, 5)
                : arrangement.expectedSignOutTime.toString().substring(0, 2),
            actualSignInTime: normalizeTime(arrangement.actualSignInTime),
            actualSignOutTime: normalizeTime(arrangement.actualSignOutTime),
          })),
        });
      });

      preparedRecords.sort((a, b) => (a.expectedDate > b.expectedDate ? -1 : a.expectedDate < b.expectedDate ? 1 : 0));

      setFinalRecords(preparedRecords);
      setActivityList(response.activities || []);
      setHolidayList(response.holidays || []);
    });
  }, [dept, month, reload, year]);

  const showEventName = () => {
    let allEvent = '<ul style="text-align: left"><li>';
    allEvent += activityList.map((elem) => `${elem.expectedDate}${elem.name}`).join('</li><li>');
    allEvent += '</li></ul>';
    swal({
      type: 'info',
      title: '3秒後自動關閉',
      html: allEvent,
      showConfirmButton: false,
      showCloseButton: false,
      timer: 3000,
    });
  };

  const handleAddStaffWorkRecord = (data) => {
    setIsRequestComplete(false);
    API.post('/Event/Adjust?timeOffset=8', data).then((response) => {
      if (response.success) {
        setIsRequestComplete(true);
        swal({ type: 'success', title: '新增成功' });
        doReload((prevReload) => !prevReload);
      } else {
        swal({ type: 'error', title: '新增失敗' });
      }
    });
  };

  const handleToggleOpenAddWorkTimeBlock = () => {
    setIsAddWorkTimeOpen(!isAddWorkTimeOpen);
  };

  const selectTable = () => {
    const tableElement = document.getElementById('allStaff');
    if (tableElement) {
      selectElementContents(tableElement);
    }
  };

  const handleInputChange = (event) => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setMonth(value);
  };

  const handleYearInputChange = (event) => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setYear(value);
  };

  const getCellTimeByMode = (arrangement) => {
    if (timeViewMode === 'expected') {
      return {
        signIn: arrangement.expectedSignInTime,
        signOut: arrangement.expectedSignOutTime,
      };
    }

    const byData = getActualTimeRangeFromData(arrangement.data);
    if (hasAnyTime(byData)) {
      return {
        signIn: byData.signIn,
        signOut: byData.signOut,
      };
    }

    if (arrangement.actualSignInTime || arrangement.actualSignOutTime) {
      return {
        signIn: arrangement.actualSignInTime || '',
        signOut: arrangement.actualSignOutTime || '',
      };
    }

    if (arrangement.hasLeave || (Array.isArray(arrangement.leaveData) && arrangement.leaveData.length > 0)) {
      return {
        signIn: arrangement.expectedSignInTime,
        signOut: arrangement.expectedSignOutTime,
      };
    }

    return {
      signIn: '',
      signOut: '',
    };
  };

  const renderStaffRows = () =>
    finalRecords.map((staff) => {
      const {
        name,
        displayName,
        arrangements,
        workDays,
        timeExpected: { hours: hoursExpected, minutes: minutesExpected },
        timeSalary: { totalSalaryTime },
      } = staff;

      const durationExpected = `${hoursExpected + Math.floor(minutesExpected / 60)}時${minutesExpected % 60}分`;
      const nameToShow = displayName || name;
      const splitName = hasLetters(nameToShow) ? [nameToShow.slice(0, 3), nameToShow.slice(3)] : [nameToShow];

      const rowOneCells = [
        <td key={`${name}-name`} rowSpan={2} className="user-name">
          {splitName.map((text, idx) => (
            <React.Fragment key={`${name}-line-${idx}`}>
              {text}
              {idx !== splitName.length - 1 && <br />}
            </React.Fragment>
          ))}
          <br />
          <span style={{ color: 'blue' }}>{typeof workDays === 'undefined' ? '0天' : `${workDays}天`}</span>
        </td>,
        <td key={`${name}-salary`} style={{ fontSize: '12px', fontWeight: 'bold' }}>
          {totalSalaryTime}小時
        </td>,
      ];

      const rowTwoCells = [
        <td key={`${name}-expected`} style={{ fontSize: '12px', fontWeight: 'bold' }}>
          {durationExpected}
        </td>,
      ];

      dayNumbers.forEach((day) => {
        const arrangementsForDate = arrangements.filter((arrangement) => arrangement.expectedDate === day);
        const indexOfDateInArrangements = findIndex(arrangements, (arrangement) => arrangement.expectedDate === day);

        if (arrangementsForDate.length === 1) {
          const eventData = arrangements[indexOfDateInArrangements];
          if (eventData.resourceId === 8) {
            rowOneCells.push(
              <td key={`${name}-${day}-type8`} rowSpan={2} bgcolor="crimson" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                例
                <br />
                假
                <br />
                <span onClick={() => handleSwitchLeave(eventData.eventId, eventData.realname, day)}>
                  <i className="fa fa-random" />
                </span>
              </td>
            );
            return;
          }

          if (eventData.resourceId === 9) {
            rowOneCells.push(
              <td key={`${name}-${day}-type9`} rowSpan={2} bgcolor="gold" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                休
                <br />
                假
                <br />
                <span onClick={() => handleSwitchLeave(eventData.eventId, eventData.realname, day)}>
                  <i className="fa fa-random" />
                </span>
              </td>
            );
            return;
          }

          if (eventData.resourceId === 10) {
            rowOneCells.push(
              <td key={`${name}-${day}-type10`} rowSpan={2} bgcolor="IndianRed" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                國定
                <br />
                排假
              </td>
            );
            return;
          }

          const overtimeStyle = eventData.hasOvertime ? { backgroundColor: 'lightblue' } : {};
          const displayTime = getCellTimeByMode(eventData);
          const shouldShowLeaveStyle = eventData.hasLeave || (timeViewMode === 'actual' && Array.isArray(eventData.leaveData) && eventData.leaveData.length > 0);
          if (eventData.absence) {
            rowOneCells.push(
              <td key={`${name}-${day}-absence-in`} bgcolor="#fbc309" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                {getDisplayTime(displayTime.signIn)}
              </td>
            );
            rowTwoCells.push(
              <td key={`${name}-${day}-absence-out`} bgcolor="#fbc309" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                {getDisplayTime(displayTime.signOut)}
              </td>
            );
            return;
          }

          if (eventData.createUserId) {
            rowOneCells.push(
              <td key={`${name}-${day}-create-in`} style={{ fontSize: '12px', fontWeight: 'bold', ...overtimeStyle }}>
                {canManageShift(currentRole) && (
                  <>
                    <span onClick={() => handleDropReason(eventData.eventId, eventData.realname, day)}>
                      <i className="fa fa-trash" />
                    </span>
                    <br />
                  </>
                )}
                {getDisplayTime(displayTime.signIn)}
              </td>
            );
            rowTwoCells.push(
              <td key={`${name}-${day}-create-out`} style={{ fontSize: '12px', fontWeight: 'bold', ...overtimeStyle }}>
                {getDisplayTime(displayTime.signOut)}
              </td>
            );
            return;
          }

          if (shouldShowLeaveStyle) {
            rowOneCells.push(
              <td key={`${name}-${day}-leave-in`} style={{ fontSize: '12px', color: 'orange', fontWeight: 'bold', ...overtimeStyle }}>
                {getDisplayTime(displayTime.signIn)}
              </td>
            );
            rowTwoCells.push(
              <td key={`${name}-${day}-leave-out`} style={{ fontSize: '12px', color: 'orange', fontWeight: 'bold', ...overtimeStyle }}>
                {getDisplayTime(displayTime.signOut)}
              </td>
            );
            return;
          }

          rowOneCells.push(
            <td key={`${name}-${day}-normal-in`} style={{ fontSize: '12px', fontWeight: 'bold' }}>
              {getDisplayTime(displayTime.signIn)}
            </td>
          );
          rowTwoCells.push(
            <td key={`${name}-${day}-normal-out`} style={{ fontSize: '12px', fontWeight: 'bold' }}>
              {getDisplayTime(displayTime.signOut)}
            </td>
          );
          return;
        }

        if (arrangementsForDate.length > 1 && arrangementsForDate.find((element) => element.resourceId === 6)) {
          const eventSchedule = arrangementsForDate.find((element) => element.resourceId === 6);
          const overtimeStyle = eventSchedule.hasOvertime ? { backgroundColor: 'lightblue' } : {};
          const displayTime = getCellTimeByMode(eventSchedule);
          const shouldShowLeaveStyle = eventSchedule.hasLeave || (timeViewMode === 'actual' && Array.isArray(eventSchedule.leaveData) && eventSchedule.leaveData.length > 0);

          if (eventSchedule.absence) {
            rowOneCells.push(
              <td key={`${name}-${day}-multi-absence-in`} bgcolor="#fbc309" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                {getDisplayTime(displayTime.signIn)}
              </td>
            );
            rowTwoCells.push(
              <td key={`${name}-${day}-multi-absence-out`} bgcolor="#fbc309" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                {getDisplayTime(displayTime.signOut)}
              </td>
            );
            return;
          }

          if (eventSchedule.createUserId) {
            rowOneCells.push(
              <td key={`${name}-${day}-multi-create-in`} bgcolor="lightsalmon" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                {canManageShift(currentRole) && (
                  <>
                    <span onClick={() => handleDropReason(eventSchedule.eventId, eventSchedule.realname, day)}>
                      <i className="fa fa-trash" />
                    </span>
                    <br />
                  </>
                )}
                {getDisplayTime(displayTime.signIn)}
              </td>
            );
            rowTwoCells.push(
              <td key={`${name}-${day}-multi-create-out`} bgcolor="lightsalmon" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                {getDisplayTime(displayTime.signOut)}
              </td>
            );
            return;
          }

          if (shouldShowLeaveStyle) {
            rowOneCells.push(
              <td key={`${name}-${day}-multi-leave-in`} style={{ fontSize: '12px', color: 'orange', fontWeight: 'bold', ...overtimeStyle }}>
                {getDisplayTime(displayTime.signIn)}
              </td>
            );
            rowTwoCells.push(
              <td key={`${name}-${day}-multi-leave-out`} style={{ fontSize: '12px', color: 'orange', fontWeight: 'bold', ...overtimeStyle }}>
                {getDisplayTime(displayTime.signOut)}
              </td>
            );
            return;
          }

          rowOneCells.push(
            <td key={`${name}-${day}-multi-normal-in`} style={{ fontSize: '12px', fontWeight: 'bold' }}>
              {getDisplayTime(displayTime.signIn)}
            </td>
          );
          rowTwoCells.push(
            <td key={`${name}-${day}-multi-normal-out`} style={{ fontSize: '12px', fontWeight: 'bold' }}>
              {getDisplayTime(displayTime.signOut)}
            </td>
          );
          return;
        }

        if (arrangementsForDate.length > 1) {
          rowOneCells.push(
            <td key={`${name}-${day}-conflict`} rowSpan={2} bgcolor="orange" style={{ fontSize: '12px', fontWeight: 'bold' }}>
              重
              <br />
              假
            </td>
          );
          return;
        }

        rowOneCells.push(
          <td key={`${name}-${day}-empty`} rowSpan={2} bgcolor="lightgreen" style={{ fontSize: '12px', fontWeight: 'bold' }}>
            空
            <br />
            班
          </td>
        );
      });

      return (
        <React.Fragment key={`staff-${name}`}>
          <tr>{rowOneCells}</tr>
          <tr>{rowTwoCells}</tr>
        </React.Fragment>
      );
    });

  const optionsMonths = [];
  for (let i = 0; i < 12; i += 1) {
    optionsMonths.push(
      <option style={{ fontSize: '20px' }} key={i} value={i}>
        {i + 1}
      </option>
    );
  }

  const optionsYears = yearNames.map(yearName => (
    <option style={{ fontSize: '20px' }} key={yearName} value={yearName}>
      {yearName}
    </option>
  ));

  const buttons = canManageShift(currentRole) ? (
    <button className="btn btn-md main-color" onClick={handleToggleOpenAddWorkTimeBlock}>
      新增員工上班紀錄
    </button>
  ) : null;

  const depts = currentDetailRole === '38' || currentDetailRole === '2' ? (
    <>
      <span>選擇部門:</span>
      <select style={{ fontSize: '20px' }} name="dept" value={dept} onChange={(e) => setDept(e.target.value)}>
        <option value="all">全部</option>
        <option value="infield">內場</option>
        <option value="outfield">外場</option>
        <option value="other">行政</option>
      </select>
    </>
  ) : null;

  return (
    <div className="u-block" style={{ display: 'inline' }}>
      <div className="u-title">
        <span>最終班表</span>
        <a
          href="https://docs.google.com/presentation/d/17IIV0wChgtlXkwQjyyp1eaSHSOpmqWGWL1XSKf6JLt4/edit?usp=sharing"
          className="u-how-use"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="fas fa-question-circle" />
        </a>
      </div>
      <div className="sign-notice">
        <div className="u-notice">註1:總工時系統會自動扣除休息時間</div>
        <div className="u-notice">註2:橘色數字表示該事件由假單新增</div>
      </div>
      <div className="shift-sheet-tabs-wrap">
        <div className="shift-sheet-tabs" role="tablist" aria-label="班表時間模式">
          <button
            type="button"
            className={`shift-sheet-tab ${timeViewMode === 'expected' ? 'is-active' : ''}`}
            onClick={() => setTimeViewMode('expected')}
          >
            預排時間
          </button>
          <button
            type="button"
            className={`shift-sheet-tab ${timeViewMode === 'actual' ? 'is-active' : ''}`}
            onClick={() => setTimeViewMode('actual')}
          >
            實際打卡
          </button>
        </div>
      </div>
      <div className="u-search-box">
        {buttons}
        <button type="button" className="btn btn-md success m-l-8" onClick={showEventName}>
          本月事件
        </button>
        <button type="button" className="btn btn-md save m-l-8" onClick={selectTable}>
          全選複製
        </button>
        <span>選擇年度:</span>
        <select style={{ fontSize: '20px' }} name="year" value={year} onChange={handleYearInputChange}>
          {optionsYears}
        </select>
        <span>選擇月份:</span>
        <select style={{ fontSize: '20px' }} name="month" value={month} onChange={handleInputChange}>
          {optionsMonths}
        </select>
        {depts}
      </div>
      <div className="row">
        <div className="col-12">
          <ScrollSync>
            <div style={{ width: '100%' }}>
              <ScrollSyncNode scroll="two-way" selfLockAxis="XY">
                <div id="calendar1" className="u-table-responsive" style={{ height: '243px', overflow: 'hidden', marginBottom: '0px' }}>
                  <table id="allStaff" className="calendar u-table u-table-border scroll--width">
                    <tbody>
                      <tr>
                        <th colSpan={dayAmount + 2}>
                          {monthNames[month]} {parseInt(year, 10)}
                        </th>
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ fontSize: '12px', fontWeight: 'bold' }}>
                          日期
                        </td>
                        {dayNumbers.map((day) => (
                          <td key={`head-day-${day}`} style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            {day}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ fontSize: '12px', fontWeight: 'bold' }}>
                          星期
                        </td>
                        {dayNumbers.map((day) => (
                          <td key={`head-week-${day}`} style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            {weekdayByDay[day]}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ fontSize: '12px', fontWeight: 'bold' }}>
                          國定假日
                        </td>
                        {dayNumbers.map((day) => {
                          const holidays = eventByDay[day] ? eventByDay[day].holidays : [];
                          const eventName = holidays.map((item) => item.name).join('\n');
                          return (
                            <td key={`head-holiday-${day}`} title={eventName} style={{ color: 'red' }}>
                              {holidays.length > 0 ? <i className="fas fa-exclamation-triangle" /> : null}
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ fontSize: '12px', fontWeight: 'bold' }}>
                          行事曆
                        </td>
                        {dayNumbers.map((day) => {
                          const activities = eventByDay[day] ? eventByDay[day].activities : [];
                          const eventName = activities.map((item) => item.name).join('\n');
                          return (
                            <td key={`head-activity-${day}`} title={eventName} style={{ color: 'red', fontSize: '12px', fontWeight: 'bold' }}>
                              {activities.length > 0 ? (
                                <span onClick={() => swal({ type: 'info', title: '活動', text: eventName.replace(/(?:\r\n|\r|\n)/g, ' ') })}>活動</span>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td colSpan={2} style={{ fontSize: '12px', fontWeight: 'bold' }}>
                          人數統計
                          <br />
                          (<span style={{ color: 'blue' }}>內場</span>,<span style={{ color: 'green' }}>外場</span>,<span style={{ color: 'red' }}>吧檯</span>)
                        </td>
                        {dayNumbers.map((day) => (
                          <td key={`head-worker-${day}`} style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            <span style={{ color: 'blue' }}>{workerCountByDay.innerFormalCount[day] || 0}</span>
                            {workerCountByDay.innerPartimeCount[day] || 0}
                            <br />
                            <span style={{ color: 'green' }}>{workerCountByDay.outsideFormalCount[day] || 0}</span>
                            {workerCountByDay.outsidePartimeCount[day] || 0}
                            <br />
                            <span style={{ color: 'red' }}>{workerCountByDay.barFormalCount[day] || 0}</span>
                            {workerCountByDay.barPartimeCount[day] || 0}
                          </td>
                        ))}
                      </tr>
                      {renderStaffRows()}
                    </tbody>
                  </table>
                </div>
              </ScrollSyncNode>
              <ScrollSyncNode scroll="syncer-only" onScroll={handleScrollChange}>
                <div id="calendar2" className="u-table-responsive" style={{ height: '450px', overflowY: 'auto' }}>
                  <table id="allStaff2" className="calendar u-table u-table-border">
                    <tbody>{renderStaffRows()}</tbody>
                  </table>
                </div>
              </ScrollSyncNode>
            </div>
          </ScrollSync>
        </div>

        <AddWorkTimeInSignInShiftBlock
          isVisible={isAddWorkTimeOpen}
          toggleOpen={handleToggleOpenAddWorkTimeBlock}
          allStaff={staffList}
          isRequestComplete={isRequestComplete}
          saveWorkRecordFunc={handleAddStaffWorkRecord}
        />
      </div>
    </div>
  );
};

export default React.memo(ShiftSheet);
